<template>
  <nav class="blog-breadcrumb" aria-label="Breadcrumb">
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="homeHref">{{ t('blog.breadcrumb.home') }}</el-breadcrumb-item>
      <el-breadcrumb-item :to="blogListHref">{{ t('blog.breadcrumb.blog') }}</el-breadcrumb-item>
      <el-breadcrumb-item v-if="articleTitle">{{ articleTitle }}</el-breadcrumb-item>
    </el-breadcrumb>
  </nav>
</template>

<script setup>
import { ensureValidSiteUrl, toLocalizedPath } from '~/utils/websiteSeo';

const props = defineProps({
  articleTitle: {
    type: String,
    default: ''
  }
});

const { t, locale } = useI18n();
const runtimeConfig = useRuntimeConfig();

const homeHref = computed(() => toLocalizedPath('/', locale.value));
const blogListHref = computed(() => toLocalizedPath('/blog', locale.value));

/**
 * 输出 BreadcrumbList 结构化数据（JSON-LD），供搜索引擎理解页面层级关系。
 * 结构化数据属渐进增强：即使 siteUrl 配置缺失，也不应阻断页面渲染。
 */
useHead(() => {
  let origin;
  try {
    origin = ensureValidSiteUrl(runtimeConfig.public.siteUrl);
  } catch {
    return {};
  }

  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: t('blog.breadcrumb.home'),
      item: `${origin}${toLocalizedPath('/', locale.value)}`
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: t('blog.breadcrumb.blog'),
      item: `${origin}${toLocalizedPath('/blog', locale.value)}`
    }
  ];

  if (props.articleTitle) {
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: props.articleTitle
    });
  }

  return {
    script: [
      {
        type: 'application/ld+json',
        textContent: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: items
        })
      }
    ]
  };
});
</script>

<style scoped lang="scss">
.blog-breadcrumb {
  position: relative;
  z-index: 2;
  padding: $spacing-md 0 $spacing-sm;

  :deep(.el-breadcrumb__item) {
    font-size: 14px;
  }

  :deep(.el-breadcrumb__inner) {
    color: $gray-700;
    font-weight: 500;

    &:hover {
      color: $primary-color;
    }
  }
}
</style>
